'use client';

/**
 * StatusPills -- Done toggle + inline star rating.
 *
 * Two variants:
 *  - InlineStatusPills: compact for item cards / list rows
 *    - "Done" pill; when active, a 5-star row appears inline with "How was it?" prompt
 *  - DetailStatusPills: larger for the item detail page
 *    - "Done" pill; when active, stars + optional review textarea appear below
 *
 * Both share the React Query cache key ['userItemStatus', itemId].
 *
 * "Liked" has been removed — rating replaces it.
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchUserItemStatus, updateUserItemStatus } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CheckCircle } from 'lucide-react';
import { StarRating } from './star-rating';

// ── Shared hook ────────────────────────────────────────────────────────────

function useStatusToggle(itemId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['userItemStatus', itemId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => fetchUserItemStatus(itemId),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (update: Parameters<typeof updateUserItemStatus>[1]) =>
      updateUserItemStatus(itemId, update),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) =>
        old ? { ...old, status: { ...old.status, ...update } } : old
      );
      return { prev };
    },
    onError: (_err, _vars, ctx: any) => {
      queryClient.setQueryData(queryKey, ctx?.prev);
      toast.error('Failed to update');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const status = data?.status ?? { done: false, rating: undefined, reviewText: undefined };
  const isDisabled = isLoading || isPending;

  function toggleDone() {
    mutate({ done: !status.done });
  }

  function setRating(value: number) {
    mutate({ rating: value === 0 ? undefined : value });
  }

  function setReview(text: string) {
    mutate({ reviewText: text || undefined });
  }

  return { status, isDisabled, toggleDone, setRating, setReview };
}

// ── Inline variant (compact, for cards and list rows) ──────────────────────

interface StatusPillsProps {
  itemId: string;
  className?: string;
}

export function InlineStatusPills({ itemId, className }: StatusPillsProps) {
  const { status, isDisabled, toggleDone, setRating } = useStatusToggle(itemId);

  // Track the previous done state to detect the 0→done transition.
  // When the user first marks Done (and has no rating yet), show the
  // "How was it?" prompt with a brief fade-in animation.
  const prevDoneRef = useRef(status.done);
  const [showPrompt, setShowPrompt] = useState(false);

  // `animate-pop` fires once per Done activation by toggling the class on/off.
  // We use a ref flag so repeated renders don't re-trigger the animation.
  const [isPoppingDone, setIsPoppingDone] = useState(false);
  const didPopDoneRef = useRef(false);

  useEffect(() => {
    const justMarkedDone = !prevDoneRef.current && status.done && !status.rating;
    if (justMarkedDone) setShowPrompt(true);
    // Hide prompt once user sets a rating
    if (status.rating) setShowPrompt(false);

    // Fire pop animation the first time this item is marked Done
    if (status.done && !didPopDoneRef.current) {
      didPopDoneRef.current = true;
      setIsPoppingDone(true);
      // Remove class after animation completes so it can re-fire if needed
      const t = setTimeout(() => setIsPoppingDone(false), 200);
      return () => clearTimeout(t);
    }
    if (!status.done) didPopDoneRef.current = false;

    prevDoneRef.current = status.done;
  }, [status.done, status.rating]);

  return (
    <div
      className={cn('flex flex-col items-end gap-1.5', className)}
      role="group"
      aria-label="Your status"
    >
      <button
        type="button"
        disabled={isDisabled}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDone(); }}
        aria-pressed={status.done}
        className={cn(
          'text-xs px-2.5 py-1 rounded-full border font-medium transition-all',
          'disabled:opacity-50 disabled:cursor-not-allowed active:scale-95',
          isPoppingDone && 'animate-pop',
          status.done
            ? 'bg-success text-success-foreground border-success'
            : 'bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground'
        )}
      >
        Done
      </button>

      {status.done && (
        <div
          className="flex flex-col items-end gap-0.5"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {/* "How was it?" prompt — animated in on first Done mark */}
          {showPrompt && (
            <span
              className="text-[10px] text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-200"
            >
              How was it?
            </span>
          )}
          <StarRating
            value={status.rating}
            onRate={setRating}
            size="sm"
          />
        </div>
      )}
    </div>
  );
}

// ── Detail variant (larger, for item detail page) ─────────────────────────

export function DetailStatusPills({ itemId, className }: StatusPillsProps) {
  const { status, isDisabled, toggleDone, setRating, setReview } = useStatusToggle(itemId);
  const [reviewDraft, setReviewDraft] = useState(status.reviewText ?? '');
  const [reviewFocused, setReviewFocused] = useState(false);

  // Pop animation fires once per Done activation (same pattern as InlineStatusPills)
  const [isPoppingDone, setIsPoppingDone] = useState(false);
  const didPopDoneRef = useRef(false);

  useEffect(() => {
    if (status.done && !didPopDoneRef.current) {
      didPopDoneRef.current = true;
      setIsPoppingDone(true);
      const t = setTimeout(() => setIsPoppingDone(false), 200);
      return () => clearTimeout(t);
    }
    if (!status.done) didPopDoneRef.current = false;
  }, [status.done]);

  function handleReviewBlur() {
    setReviewFocused(false);
    const trimmed = reviewDraft.trim();
    if (trimmed !== (status.reviewText ?? '')) {
      setReview(trimmed);
    }
  }

  return (
    <div className={cn('space-y-3', className)} role="group" aria-label="Your status">
      {/* Done pill */}
      <button
        type="button"
        disabled={isDisabled}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDone(); }}
        aria-pressed={status.done}
        className={cn(
          'flex items-center gap-2 px-5 py-3 rounded-xl border font-medium transition-all cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed active:scale-95',
          isPoppingDone && 'animate-pop',
          status.done
            ? 'bg-success text-success-foreground border-success'
            : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
        )}
      >
        {status.done ? (
          /* Filled circle + visible white tick */
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="white" fillOpacity="0.25" />
            <path
              d="M7.5 12.5l3 3 6-6"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <CheckCircle className="h-5 w-5" strokeWidth={1.5} />
        )}
        <span className="text-sm">Done</span>
      </button>

      {/* Inline rating + review — only visible when done */}
      {status.done && (
        <div
          className="pl-1 space-y-2"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <StarRating
            value={status.rating}
            onRate={setRating}
            size="md"
          />
          <textarea
            placeholder="Your take on this… (optional)"
            value={reviewFocused ? reviewDraft : (status.reviewText ?? reviewDraft)}
            onChange={(e) => setReviewDraft(e.target.value)}
            onFocus={() => {
              setReviewDraft(status.reviewText ?? '');
              setReviewFocused(true);
            }}
            onBlur={handleReviewBlur}
            rows={2}
            maxLength={1000}
            className={cn(
              'w-full text-sm rounded-r-lg px-3 py-2 resize-none focus:outline-none transition-colors',
              'bg-[oklch(0.97_0.04_85)] border-l-2 border-accent dark:bg-[oklch(0.22_0.04_85)]',
              'placeholder:text-muted-foreground/50',
              'focus:border-accent/80'
            )}
          />
        </div>
      )}
    </div>
  );
}
